import { makeStyles } from 'tss-react/mui';
import { round, sumBy } from 'lodash';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import AmendmentsUpload from './AmendmentsUpload';
import Button from 'components/Button';
import { ConsiderationType } from 'further-types/exit';
import type { ExitDetailsFormValues } from '../ExitDetailsForm';
import type { ShareConsideration } from '../types';

const useStyles = makeStyles()(() => ({
  flex: {
    marginTop: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  buttonsFlex: {
    display: 'flex',
    alignItems: 'center',
    gridGap: '1rem',
  },
  editedExitDisclosureContent: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: '1rem',
  },
}));

type Props = {
  exitDetails: any;
  shareholdings: Array<any>;
  setShareholdings: React.Dispatch<React.SetStateAction<Array<any>>>;
  shareConsiderations?: Array<ShareConsideration>;
  setShareConsiderations?: React.Dispatch<
    React.SetStateAction<Array<ShareConsideration>>
  >;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
  disableTable: boolean;
  isBeingEdited: boolean;
  isBeingCorrected: boolean;
  previousExitDetails?: ExitDetailsFormValues;
};

const ButtonsAndErrorModals: React.FC<Props> = ({
  exitDetails,
  shareholdings,
  setShareholdings,
  shareConsiderations,
  setShareConsiderations,
  onSubmit,
  onCancel,
  isLoading,
  disableTable,
  isBeingEdited,
  isBeingCorrected,
  previousExitDetails,
}) => {
  const { classes } = useStyles();
  const cancelConfirmDisclosure = useDisclosure();
  const checkEachShareholdingAllocationDisclosure = useDisclosure();
  const checkTotalSharesDisclosure = useDisclosure();
  const checkAccruedFeesMaxDisclosure = useDisclosure();
  const checkTotalAccruedFeesPerSubscriptionDisclosure = useDisclosure();
  const checkTotalsToBeReturnedDisclosure = useDisclosure();
  const editedExitDisclosure = useDisclosure();
  const negativeAdditionalValueDisclosure = useDisclosure();
  const checkTotalNewSharesAllocatedDisclosure = useDisclosure();
  const considerationWithoutSharesSoldDisclosure = useDisclosure();

  const checkTotalSharesAllocatedMatchesSharesToBeSold = () => {
    // For share consideration exits, sum from shareConsiderations; for cash, sum from shareholdings
    const itemsToSum =
      exitDetails?.considerationType === ConsiderationType.Share
        ? shareConsiderations ?? []
        : shareholdings;
    const totalSharesAllocated = round(
      sumBy(
        itemsToSum,
        (item: { sharesAllocatedForSale: number }) =>
          item.sharesAllocatedForSale,
      ),
      10,
    );
    if (totalSharesAllocated !== exitDetails.sharesToBeSold) {
      return false;
    }
    return true;
  };

  const checkEachShareholdingAllocation = () =>
    shareholdings.every((sh) => sh.sharesAllocatedForSale <= sh.noOfShares);

  const checkEachAccruedFeesBelowMax = () =>
    shareholdings.every(
      (sh) => sh.accruedFeesCharged <= sh.investorTotalAccruedFees,
    );

  const checkTotalAccruedFeesPerSubscriptionBelowMax = () => {
    // Group shareholdings by investment ID and check that the sum of accrued fees
    // for each investment doesn't exceed the total available for that subscription.
    // Skip rows with missing investmentId so they don't get aggregated under 'unknown'.
    const totals = new Map<string, { charged: number; max: number }>();

    for (const sh of shareholdings) {
      if (!sh.investmentId) continue;
      const investmentId = String(sh.investmentId);
      if (!totals.has(investmentId)) {
        totals.set(investmentId, {
          charged: 0,
          max: sh.investorTotalAccruedFees ?? 0,
        });
      }
      const entry = totals.get(investmentId)!;
      entry.charged += sh.accruedFeesCharged ?? 0;
    }

    return [...totals.values()].every(({ charged, max }) => charged <= max);
  };

  const checkEachTotalToBeReturnedAboveZero = () =>
    shareholdings.every((sh) => sh.totalToBeReturned >= 0);

  const checkEachAdditionalReceivedValueAboveZero = () =>
    shareholdings.every((sh) => sh.additionalGrossReceivedValue >= 0);

  const checkTotalNewSharesAllocatedMatchesTotalConsiderationShares = () => {
    if (!shareConsiderations || !exitDetails?.totalConsiderationShares) {
      return true;
    }
    const totalNewSharesAllocated = round(
      sumBy(shareConsiderations, (item) => Number(item.newSharesAllocated)),
      10,
    );
    const totalConsiderationShares = Number(
      exitDetails.totalConsiderationShares,
    );
    return totalNewSharesAllocated === totalConsiderationShares;
  };

  const checkConsiderationWithoutSharesSold = () => {
    if (!shareConsiderations) {
      return false;
    }
    return shareConsiderations.some(
      (item) =>
        Number(item.newSharesAllocated) > 0 &&
        Number(item.sharesAllocatedForSale ?? 0) === 0,
    );
  };

  const validate = () => {
    // For share consideration exits, validate both total shares allocated and total new shares allocated
    if (exitDetails?.considerationType === ConsiderationType.Share) {
      if (!checkTotalSharesAllocatedMatchesSharesToBeSold()) {
        checkTotalSharesDisclosure.onOpen();
        return false;
      }
      if (!checkTotalNewSharesAllocatedMatchesTotalConsiderationShares()) {
        checkTotalNewSharesAllocatedDisclosure.onOpen();
        return false;
      }
      // Check for warning: investors receiving consideration without selling shares
      if (checkConsiderationWithoutSharesSold()) {
        considerationWithoutSharesSoldDisclosure.onOpen();
        return false;
      }
      // Skip all other validations and dialogs for share consideration - submit directly
      onSubmit();
      return;
    }

    // Cash consideration validations (existing logic)
    if (!isBeingEdited && !checkEachShareholdingAllocation()) {
      checkEachShareholdingAllocationDisclosure.onOpen();
      return false;
    }
    if (!checkEachAccruedFeesBelowMax()) {
      checkAccruedFeesMaxDisclosure.onOpen();
      return false;
    }
    if (!checkTotalAccruedFeesPerSubscriptionBelowMax()) {
      checkTotalAccruedFeesPerSubscriptionDisclosure.onOpen();
      return false;
    }
    if (!checkEachTotalToBeReturnedAboveZero()) {
      checkTotalsToBeReturnedDisclosure.onOpen();
      return false;
    }
    if (!checkTotalSharesAllocatedMatchesSharesToBeSold()) {
      checkTotalSharesDisclosure.onOpen();
      return false;
    }

    if (isBeingEdited) {
      // Skip the negative additional received value check when transitioning from
      // a share consideration exit to a cash consideration exit, as the concept
      // of "additional received value" doesn't apply in this context
      const previousConsiderationType = previousExitDetails?.considerationType;
      const currentConsiderationType = exitDetails?.considerationType;
      const isTransitioningFromShareToCash =
        previousConsiderationType === ConsiderationType.Share &&
        currentConsiderationType === ConsiderationType.Cash;

      if (
        !isTransitioningFromShareToCash &&
        !checkEachAdditionalReceivedValueAboveZero()
      ) {
        negativeAdditionalValueDisclosure.onOpen();
      } else {
        editedExitDisclosure.onOpen();
      }
    } else {
      onSubmit();
    }
  };

  return (
    <>
      <div className={classes.flex} style={{ justifyContent: 'space-between' }}>
        <AmendmentsUpload
          shareholdings={shareholdings}
          setShareholdings={setShareholdings}
          shareConsiderations={shareConsiderations}
          setShareConsiderations={setShareConsiderations}
          exitDetails={exitDetails}
          disableTable={disableTable}
          isBeingEdited={isBeingEdited || isBeingCorrected}
        />

        <div className={classes.buttonsFlex}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={cancelConfirmDisclosure.onOpen}
            disabled={isLoading}
          >
            Reprocess
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={validate}
            disabled={
              exitDetails?.considerationType === ConsiderationType.Share
                ? !shareConsiderations?.length
                : !shareholdings.length
            }
            loading={isLoading}
          >
            {isBeingCorrected ? 'Save corrected exit' : 'Submit'}
          </Button>
        </div>
      </div>
      <AlertDialog
        title="Re-processing exit"
        content="If you continue, you will lose any data you have entered in the results table. This cannot be undone."
        open={cancelConfirmDisclosure.isOpen}
        onClose={cancelConfirmDisclosure.onClose}
        onConfirm={() => {
          onCancel();
          cancelConfirmDisclosure.onClose();
        }}
        btnLabels={{ cancel: 'Cancel', confirm: 'Confirm' }}
      />
      <AlertDialog
        title="The total new shares allocated does not match the total consideration shares."
        content="Please adjust the new shares allocated for each investor so that the total matches the total consideration shares entered in the form."
        open={checkTotalNewSharesAllocatedDisclosure.isOpen}
        onClose={checkTotalNewSharesAllocatedDisclosure.onClose}
        btnLabels={{ confirm: false, cancel: 'OK' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
      />
      <AlertDialog
        title="The number of shares you have allocated for sale varies from your originally requested figure."
        content="Please go back and adjust individual allocations or re-process this exit with a revised number of shares."
        open={checkTotalSharesDisclosure.isOpen}
        onClose={checkTotalSharesDisclosure.onClose}
        btnLabels={{ confirm: false, cancel: 'OK' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
      />
      <AlertDialog
        title="One or more investors has a sale allocation greater than their shareholding."
        content="Please adjust the individual allocations shown in red."
        open={checkEachShareholdingAllocationDisclosure.isOpen}
        onClose={checkEachShareholdingAllocationDisclosure.onClose}
        btnLabels={{ confirm: false, cancel: 'OK' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
      />
      <AlertDialog
        title="One or more investors has an amount of accrued fees entered that is higher than their their outstanding accrued fees."
        content="Please adjust the accrued fees charged shown in red."
        open={checkAccruedFeesMaxDisclosure.isOpen}
        onClose={checkAccruedFeesMaxDisclosure.onClose}
        btnLabels={{ confirm: false, cancel: 'OK' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
      />
      <AlertDialog
        title="Total accrued fees exceed the available amount for one or more subscriptions."
        content="The sum of accrued fees charged across all shareholdings in a subscription cannot exceed the total accrued fees available for that subscription. Please adjust the fee allocations."
        open={checkTotalAccruedFeesPerSubscriptionDisclosure.isOpen}
        onClose={checkTotalAccruedFeesPerSubscriptionDisclosure.onClose}
        btnLabels={{ confirm: false, cancel: 'OK' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
      />
      <AlertDialog
        title="One or more investors has an amount to be returned below zero."
        content="Please amend the accrued fees to be charged."
        open={checkTotalsToBeReturnedDisclosure.isOpen}
        onClose={checkTotalsToBeReturnedDisclosure.onClose}
        btnLabels={{ confirm: false, cancel: 'OK' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
      />
      <AlertDialog
        title="Please note"
        content={
          <span>
            One or more rows in this exit have a negative{' '}
            <i>additional received value</i> figure. If you proceed, negative
            amounts will be deducted from each relevant investor's cash balance.
          </span>
        }
        open={negativeAdditionalValueDisclosure.isOpen}
        onClose={negativeAdditionalValueDisclosure.onClose}
        onConfirm={() => {
          negativeAdditionalValueDisclosure.onClose();
          editedExitDisclosure.onOpen();
        }}
        btnLabels={{ confirm: 'Proceed', cancel: 'Go back' }}
        cancelBtnProps={{ color: 'primary' }}
      />
      <AlertDialog
        title="Please note"
        content={
          <div className={classes.editedExitDisclosureContent}>
            <p>Upon processing this change:</p>
            <p>
              (i) No future edit can reduce the{' '}
              <i>Received value share price</i> below its revised level
            </p>
            <p>
              (ii) Any future increase to the <i>Taxable value share price</i>{' '}
              will trigger a new tax event in each client's annual tax report
            </p>
          </div>
        }
        open={editedExitDisclosure.isOpen}
        onClose={editedExitDisclosure.onClose}
        onConfirm={() => {
          editedExitDisclosure.onClose();
          onSubmit();
        }}
        btnLabels={{ confirm: 'Proceed', cancel: 'Go back' }}
        cancelBtnProps={{ color: 'primary' }}
      />
      <AlertDialog
        title="Consideration received without shares sold"
        content="One or more investors are receiving consideration without selling shares. Please check this before proceeding."
        open={considerationWithoutSharesSoldDisclosure.isOpen}
        onClose={considerationWithoutSharesSoldDisclosure.onClose}
        onConfirm={() => {
          considerationWithoutSharesSoldDisclosure.onClose();
          onSubmit();
        }}
        btnLabels={{ confirm: 'Submit anyway', cancel: 'Go back' }}
        cancelBtnProps={{ color: 'primary' }}
      />
    </>
  );
};

export default ButtonsAndErrorModals;
